import React from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { colors } from '../styles/colors';

const NavigationMenu = styled.nav`
  position: absolute;
  top: 35px;
  left: 60px;
  z-index: 10;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 60px;

  @media (max-width: 768px) {
    top: 25px;
    left: 30px;
    gap: 40px;
  }

  @media (max-width: 1050px) {
    display: none; /* Hide navigation menu at 1050px */
  }
`;

const NavLogo = styled.img`
  height: 45px;
  width: auto;
  margin-top: -8px;
  cursor: pointer;

  @media (max-width: 768px) {
    height: 35px;
  }

  @media (max-width: 480px) {
    height: 30px;
  }
`;

const NavItems = styled.div`
  display: flex;
  flex-direction: row;
  gap: 30px;

  @media (max-width: 768px) {
    gap: 20px;
  }

  @media (max-width: 480px) {
    gap: 15px;
  }
`;

const MenuItem = styled.a<{ $textColor?: string }>`
  font-family: 'GremeryTwo', cursive;
  font-size: 1.2rem;
  color: ${props => props.$textColor || colors.primary.gold};
  text-decoration: none;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateX(5px);
  }

  @media (max-width: 1180px) {
    font-size: 1.1rem;
  }

  @media (max-width: 1000px) {
    font-size: 1rem;
  }

  @media (max-width: 840px) {
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const MenuLink = styled(Link)<{ $textColor?: string }>`
  font-family: 'GremeryTwo', cursive;
  font-size: 1.2rem;
  color: ${props => props.$textColor || colors.primary.gold};
  text-decoration: none;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateX(5px);
  }

  @media (max-width: 1180px) {
    font-size: 1.1rem;
  }

  @media (max-width: 1000px) {
    font-size: 1rem;
  }

  @media (max-width: 840px) {
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

interface NavigationProps {
  onLogoClick?: () => void;
  textColor?: string;
  logoPath?: string;
}

const Navigation: React.FC<NavigationProps> = ({ onLogoClick, textColor, logoPath = "/images/agave-full-logo.png" }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogoClick = () => {
    if (onLogoClick) {
      onLogoClick();
    } else {
      if (location.pathname !== '/') {
        navigate('/');
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const scrollToSection = (sectionId: string) => {
    const attemptScroll = (retries = 0, maxRetries = 10) => {
      const element = document.getElementById(sectionId);
      if (element) {
        // Small delay to ensure any layout shifts are complete
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
      } else if (retries < maxRetries) {
        // Retry after 100ms if element not found
        setTimeout(() => attemptScroll(retries + 1, maxRetries), 100);
      }
    };

    if (location.pathname !== '/') {
      navigate('/');
      // Wait longer for navigation to complete and page to render
      setTimeout(() => attemptScroll(), 300);
    } else {
      attemptScroll();
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setTimeout(() => window.scrollTo(0, 0), 0);
  };

  return (
    <NavigationMenu>
      <NavLogo 
        src={logoPath}
        alt="Agave Logo" 
        onClick={handleLogoClick}
      />
      <NavItems>
        <MenuItem onClick={() => scrollToSection('schedule')} $textColor={textColor}>Schedule</MenuItem>
        <MenuLink to="/travel" onClick={() => handleNavigation('/travel')} $textColor={textColor}>Details</MenuLink>
        <MenuLink to="/rsvp" onClick={() => handleNavigation('/rsvp')} $textColor={textColor}>RSVP</MenuLink>
        <MenuLink to="/registry" onClick={() => handleNavigation('/registry')} $textColor={textColor}>Registry</MenuLink>
      </NavItems>
    </NavigationMenu>
  );
};

export default Navigation;
